import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

import {
  fetchAnalysisDetails,
  fetchAnalysisData,
} from "../Services/apiService";

import ReportContent from "../pages/admin/report/ReportContent";

const DownloadDialog = ({ open, onClose, orderId, testId }) => {
  const [loading, setLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportRef = useRef(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Download PDF via the shared ReportContent ref
  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      await reportRef.current?.handleGenerateReport();
    } catch (err) {
      console.error("Error generating PDF:", err);
    } finally {
      setPdfLoading(false);
    }
  };

  // Fetch analysis data when dialog opens
  useEffect(() => {
    if (open && orderId && testId) {
      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);
          setDataFetched(false);
          setReportData(null);

          console.log("📥 Auto-fetching data for:", { orderId, testId });

          const formData = await fetchAnalysisDetails(orderId, testId);
          const analysisData = await fetchAnalysisData(orderId, testId);

          console.log("✅ Form Data Response:", formData);
          console.log("✅ Analysis Data Response:", analysisData);

          const transformedAnalysisData = transformAnalysisData(analysisData);

          const completeReportData = {
            formData: formData,
            analysisData: transformedAnalysisData,
            additionalInfo: {
              date_evaluation:
                analysisData?.data?.additional_info?.date_evaluation ||
                new Date().toISOString().split("T")[0],
              date_of_birth:
                analysisData?.data?.additional_info?.date_of_birth ||
                formData?.mandatory_information?.date_of_birth,
              date_of_sampling:
                analysisData?.data?.additional_info?.date_of_sampling ||
                formData?.submissionTimestamp,
              evaluated_by:
                analysisData?.data?.additional_info?.evaluated_by || "",
            },
            notes: analysisData?.data?.notes || null,
            metadata: {
              orderId: orderId,
              testId: testId,
              generatedAt: new Date().toISOString(),
            },
          };

          setReportData(completeReportData);
          setDataFetched(true);
        } catch (err) {
          console.error("❌ Error fetching download data:", err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [open, orderId, testId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setReportData(null);
      setDataFetched(false);
      setError(null);
    }
  }, [open]);

  // Transform API analysis data into the shape ReportContent expects
  const transformAnalysisData = (apiData) => {
    if (!apiData?.data?.analysis_data) return null;

    const samples = apiData.data.analysis_data;
    const h2_values = [];
    const ch4_values = [];
    const co2_values = [];

    for (let i = 1; i <= 10; i++) {
      const sample = samples[`sample_${i}`];
      if (sample) {
        h2_values.push(sample.h2 === "-" ? "-" : String(sample.h2));
        ch4_values.push(sample.ch4 === "-" ? "-" : String(sample.ch4));
        co2_values.push(sample.co2 === "-" ? "-" : String(sample.co2));
      } else {
        h2_values.push("-");
        ch4_values.push("-");
        co2_values.push("-");
      }
    }

    return { h2_values, ch4_values, co2_values };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        Report Preview — Order {orderId} / Test {testId}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1, sm: 3 }, overflowX: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading report data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error">Error: {error}</Typography>
          </Box>
        ) : reportData ? (
          /* Shared report content — same component as the full-page report */
          <ReportContent
            ref={reportRef}
            reportData={reportData}
            orderId={orderId}
            testId={testId}
          />
        ) : (
          <Typography>No data available</Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {reportData && (
          <Button
            variant="contained"
            startIcon={
              pdfLoading ? <CircularProgress size={20} /> : <DownloadIcon />
            }
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
          >
            {pdfLoading ? "Generating PDF..." : "Download PDF"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DownloadDialog;
